import React, { useRef } from "react";
import { Form, Button, Label } from "semantic-ui-react";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EnvironmentalRiskMatrixGriform } from "../../../../api";
import { initialValues, validationSchema } from "./GriForm.form";
import "../EnvironmentalRiskMatrixForm/EnvironmentalRiskMatrixForm.scss";

const environmentalRiskMatrixGriCtrl = new EnvironmentalRiskMatrixGriform();

// Misma fórmula que server/controllers/environmentalRiskMatrixGri.js —
// preview puramente informativo, el valor real se recalcula en el servidor.
function computePreview(values) {
  const isNegative = values.sign !== "Positivo";
  const isPotential = values.status === "Potencial (podría ocurrir)";

  const scale = Number(values.scale) || 0;
  const scope = Number(values.scope) || 0;
  const irremediability = isNegative ? Number(values.irremediability) || 0 : 0;

  const factors = (isNegative ? [scale, scope, irremediability] : [scale, scope]).filter((x) => x > 0);
  let severity = 0;
  if (factors.length) {
    const avg = factors.reduce((a, b) => a + b, 0) / factors.length;
    const max = Math.max(...factors);
    severity = Math.max(1, Math.min(5, Math.round((avg + max) / 2)));
  }
  const probability = isPotential ? Number(values.rawProbability) || 0 : 5;

  return { severity, probability, level: severity * probability };
}

export function GriForm(props) {
  const { onClose, onReload, item, siteSelected } = props;

  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const submittingRef = useRef(false);

  const signOptions = [
    { key: "Negativo", value: "Negativo", text: t("risk_matrix_gri_sign_negative") },
    { key: "Positivo", value: "Positivo", text: t("risk_matrix_gri_sign_positive") },
  ];
  const statusOptions = [
    { key: "Real (ya ocurre)", value: "Real (ya ocurre)", text: t("risk_matrix_gri_status_real") },
    { key: "Potencial (podría ocurrir)", value: "Potencial (podría ocurrir)", text: t("risk_matrix_gri_status_potential") },
  ];
  const scaleOptions = [1, 2, 3, 4, 5].map((n) => ({ key: n, value: n, text: String(n) }));

  const formik = useFormik({
    initialValues: initialValues(item),
    validationSchema: validationSchema(t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      if (submittingRef.current) return;
      submittingRef.current = true;
      try {
        if (!item) {
          const res = await environmentalRiskMatrixGriCtrl.createEnvironmentalRiskMatrixGri(accessToken, {
            ...formValue,
            site: user?.site || siteSelected,
          });
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        } else {
          const res = await environmentalRiskMatrixGriCtrl.updateEnvironmentalRiskMatrixGri(accessToken, item._id, formValue);
          if (res.code === 200) toast.success(t("form_saved_successfully"));
        }
        setTimeout(() => {
          onReload?.();
          onClose?.();
        }, 800);
      } catch (error) {
        toast.error(error?.msg || error?.message || t("server_connection_error"));
      } finally {
        submittingRef.current = false;
      }
    },
  });

  const isNegative = formik.values.sign !== "Positivo";
  const isPotential = formik.values.status === "Potencial (podría ocurrir)";
  const preview = computePreview(formik.values);

  return (
    <Form className="risk-matrix-form" onSubmit={formik.handleSubmit}>
      <h3 className="risk-matrix-form__title">{item ? t("risk_matrix_edit_item") : t("risk_matrix_new_item")}</h3>
      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.title && !!formik.errors.title}>
          <label>{t("title")}</label>
          <Form.Input
            name="title"
            placeholder={t("risk_matrix_title_placeholder")}
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
          {formik.touched.title && formik.errors.title && (
            <Label basic color="red" pointing>{formik.errors.title}</Label>
          )}
        </Form.Field>

        <Form.Field>
          <label>{t("risk_matrix_gri_sign")}</label>
          <Form.Select
            options={signOptions}
            value={formik.values.sign}
            onChange={(_, { value }) => formik.setFieldValue("sign", value)}
          />
        </Form.Field>
      </div>

      <Form.Field>
        <label>{t("description")}</label>
        <Form.TextArea name="description" value={formik.values.description} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__row">
        <Form.Field>
          <label>{t("risk_matrix_gri_status")}</label>
          <Form.Select
            options={statusOptions}
            value={formik.values.status}
            onChange={(_, { value }) => formik.setFieldValue("status", value)}
          />
        </Form.Field>

        {isPotential && (
          <Form.Field error={formik.touched.rawProbability && !!formik.errors.rawProbability}>
            <label>{t("risk_matrix_probability")}</label>
            <Form.Select
              options={scaleOptions}
              placeholder="1-5"
              value={formik.values.rawProbability}
              onChange={(_, { value }) => formik.setFieldValue("rawProbability", value)}
              onBlur={() => formik.setFieldTouched("rawProbability", true)}
            />
            {formik.touched.rawProbability && formik.errors.rawProbability && (
              <Label basic color="red" pointing>{formik.errors.rawProbability}</Label>
            )}
          </Form.Field>
        )}
      </div>

      <div className="risk-matrix-form__row">
        <Form.Field error={formik.touched.scale && !!formik.errors.scale}>
          <label>{t("risk_matrix_gri_scale")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.scale}
            onChange={(_, { value }) => formik.setFieldValue("scale", value)}
            onBlur={() => formik.setFieldTouched("scale", true)}
          />
          {formik.touched.scale && formik.errors.scale && (
            <Label basic color="red" pointing>{formik.errors.scale}</Label>
          )}
        </Form.Field>

        <Form.Field error={formik.touched.scope && !!formik.errors.scope}>
          <label>{t("risk_matrix_gri_scope")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.scope}
            onChange={(_, { value }) => formik.setFieldValue("scope", value)}
            onBlur={() => formik.setFieldTouched("scope", true)}
          />
          {formik.touched.scope && formik.errors.scope && (
            <Label basic color="red" pointing>{formik.errors.scope}</Label>
          )}
        </Form.Field>
      </div>

      {isNegative && (
        <Form.Field error={formik.touched.irremediability && !!formik.errors.irremediability}>
          <label>{t("risk_matrix_gri_irremediability")}</label>
          <Form.Select
            options={scaleOptions}
            placeholder="1-5"
            value={formik.values.irremediability}
            onChange={(_, { value }) => formik.setFieldValue("irremediability", value)}
            onBlur={() => formik.setFieldTouched("irremediability", true)}
          />
          {formik.touched.irremediability && formik.errors.irremediability && (
            <Label basic color="red" pointing>{formik.errors.irremediability}</Label>
          )}
        </Form.Field>
      )}

      {(formik.values.scale || formik.values.scope) && (
        <div className="risk-matrix-form__preview">
          <span>{t("risk_matrix_severity")}: <b>{preview.severity}</b></span>
          <span>{t("risk_matrix_level")}: <b>{preview.level}</b></span>
        </div>
      )}

      <Form.Field>
        <label>{t("risk_matrix_controls")}</label>
        <Form.TextArea name="controls" value={formik.values.controls} onChange={formik.handleChange} />
      </Form.Field>

      <Form.Field>
        <label>{t("risk_matrix_action")}</label>
        <Form.TextArea name="action" value={formik.values.action} onChange={formik.handleChange} />
      </Form.Field>

      <div className="risk-matrix-form__actions">
        <Button type="submit" primary loading={formik.isSubmitting} disabled={formik.isSubmitting}>
          {!item ? t("save") : t("update")}
        </Button>
        <Button type="button" onClick={() => onClose?.()}>
          {t("cancel")}
        </Button>
      </div>
    </Form>
  );
}
